import { useDataInputDialog } from "client/Hooks";
import {
	PropsWithChildren,
	ReactElement,
	useCallback
} from "react";
import { Dialog } from "../Dialog/Dialog";
import { renderReactDialog } from "../DialogRenderer/DialogRenderer";
import { Toolbar } from "../Toolbar/Toolbar";
import styles from "./AlertDialog.module.less";

export type AlertDialogProps = {
	title: string | ReactElement;
	confirmText?: string;
	onClose: () => void;
	showCloseButton?: boolean;
	trackingId: string;
};

export const AlertDialog = (props: PropsWithChildren<AlertDialogProps>) => {
	const {
		title,
		confirmText,
		children,
		onClose,
		showCloseButton,
		trackingId
	} = props;

	return (
		<Dialog trackingId={trackingId}>
			<Dialog.Header title={title} onClose={showCloseButton ? onClose : undefined} />
			<Dialog.Body>
				<div className={styles.content}>
					{children}
				</div>
			</Dialog.Body>
			<Dialog.Footer>
				{confirmText && (
					<Toolbar>
						<Toolbar.Button
							kind="primary"
							text={confirmText}
							onClick={onClose}
							trackingId="close"
						/>
					</Toolbar>
				)}
			</Dialog.Footer>
		</Dialog>
	);
};
AlertDialog.displayName = "AlertDialog";

export type AlertDialogInitialData = Omit<
	PropsWithChildren<AlertDialogProps>,
	"onClose"
>;

export function useAlertDialog(): [
	element: ReactElement | null,
	open: (props: AlertDialogInitialData) => Promise<void>
] {
	const [element, originalOpen]= useDataInputDialog<AlertDialogInitialData, void>(
		(props, onClose) => (
			<AlertDialog
				{...props}
				onClose={onClose}
			/>
		)
	);

	const open = useCallback(
		async (props: AlertDialogInitialData): Promise<void> => {
			await originalOpen(props);
		},
		[originalOpen]
	);

	return [element, open];
}

export function openAlertDialog(props: AlertDialogInitialData): Promise<void> {
	return new Promise<void>(
		(resolve): void => {
			renderReactDialog(
				(onClose) => (
					<AlertDialog
						{...props}
						onClose={() => {
							onClose();
							resolve();
						}}
					/>
				)
			);
		}
	);
}
